import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { getMenu } from 'app/core/customer-menus/menu-utilities';
import { MenuItem } from 'app/core/models/menu/menu.model';
import { ROUTE_IDENTIFIER_NAMES } from 'app/core/models/route-identifiers/route-identifiers.model';
import { SecurityRole } from 'app/core/models/security/security-role';
import { AuthService } from 'app/core/services/auth/auth.service';
import { BrowserStorageService } from 'app/core/utilities';
import { combineLatest } from 'rxjs';
import { MenuItemComponent } from '../menu-item/menu-item.component';

@Component({
  selector: 'sqep-custom-sidenav',
  imports: [
    MatListModule,
    MatSidenavModule,
    RouterModule,
    MatIconModule,
    MenuItemComponent,
  ],
  templateUrl: './custom-sidenav.component.html',
  styleUrl: './custom-sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSidenavComponent implements OnInit {
  readonly clientLogoAsset2 = input<string>();
  readonly heading = input<string>();
  readonly subTitle = input<string>();
  readonly applicationName = input<string>();
  readonly collapsed = input<boolean>(false);

  private readonly storageService = inject(BrowserStorageService);
  private readonly authService = inject(AuthService);
  private securityRoleLookup: { [role_identifier: string]: SecurityRole };
  private userSecurityLevel: number;
  private isManager: boolean;
  private isAssessor: boolean;
  private isTrainingProviderAdmin: boolean;
  private isSuperuser: boolean;

  protected readonly menuData = signal<MenuItem[]>([]);
  protected readonly profilePicSize = computed(() =>
    this.collapsed() ? '32' : '64',
  );
  protected changeCollapsedState = output<boolean>();
  protected readonly selectedMenuItem = signal<string>(
    this.storageService.get('lastSelectedMenuItem'),
  );

  ngOnInit(): void {
    combineLatest([
      this.authService.securityRoleLookupSubj,
      this.authService.loggedInUserSubj,
    ]).subscribe({
      next: ([securityRole, loggedInUser]) => {
        this.securityRoleLookup = securityRole;
        this.userSecurityLevel = loggedInUser.securityLevel;
        this.isManager = loggedInUser.managerTypeId > 0;
        this.isAssessor = loggedInUser.isAssessor;
        this.isTrainingProviderAdmin = this.authService.isTrainingProviderAdmin;
        this.isSuperuser = this.authService.isSuperuser;
        this.menuData.set(
          [...getMenu()]
            .filter((m) => this.showMenuItem(m))
            .map((element) => {
              return {
                ...element,
                subMenu: element.subMenu?.filter((subElement) =>
                  this.showMenuItem(subElement),
                ),
              };
            }),
        );
      },
    });
  }

  private showMenuItem(menuItem: MenuItem): boolean {
    const isTrainingProvidersItem =
      menuItem.routeIdentifier === ROUTE_IDENTIFIER_NAMES.LMS_VIEW_PROVIDERS ||
      menuItem.routeIdentifier === ROUTE_IDENTIFIER_NAMES.LMS;

    // TP admins and superusers always see the Training Management parent and child
    // regardless of hideItem or securityRoles configured for that item.
    if (
      (this.isTrainingProviderAdmin || this.isSuperuser) &&
      isTrainingProvidersItem
    ) {
      return true;
    }

    if (menuItem.hideItem) {
      return false;
    }

    const hasManagementAccess =
      menuItem.allowManagementPositionAccess && this.isManager;

    let hasRequiredSecurityLevel: boolean;
    if (Array.isArray(menuItem.securityRoles)) {
      hasRequiredSecurityLevel = menuItem.securityRoles.some(
        (role) =>
          this.securityRoleLookup[role]?.securityLevel ===
          this.userSecurityLevel,
      );
    } else {
      hasRequiredSecurityLevel =
        this.securityRoleLookup[menuItem.securityRoles]?.securityLevel <=
        this.userSecurityLevel;
    }

    const visible = menuItem.forAssessorOnly
      ? hasRequiredSecurityLevel && this.isAssessor
      : hasRequiredSecurityLevel || hasManagementAccess;

    if (!visible || !this.isTrainingProviderAdmin || this.isSuperuser) {
      return visible;
    }

    // TP admin: visible passed the security level check (same level as Admin).
    // Block Admin-exclusive items — array-role items use exact-level matching
    // so they are Admin-only by design.
    if (Array.isArray(menuItem.securityRoles)) {
      return false;
    }

    // For range-based checks (>=), only show items accessible to a plain User.
    const minRequiredLevel =
      this.securityRoleLookup[menuItem.securityRoles]?.securityLevel ?? 0;
    const userLevel =
      this.securityRoleLookup['SECURITY_ROLE_USER']?.securityLevel ?? 0;
    return minRequiredLevel <= userLevel;
  }
}
